import type { PnlEvent } from "./useStatsHistory";

// Реализованный P&L раскладываем на три источника — как в дизайне «Статистики»:
// купоны, продажи (по своей воле) и погашения/амортизации (инициирует эмитент).
export type Source = "coupons" | "sells" | "redemptions";

export function sourceOf(type: PnlEvent["trade_type"]): Source {
  if (type === "coupon") return "coupons";
  if (type === "sell") return "sells";
  return "redemptions"; // redeem + amort
}

export type StatsSummary = {
  total: number; // накопленный реализованный P&L (весь период)
  ytd: number; // с начала текущего года
  month: number; // за текущий месяц
  coupons: number;
  sells: number;
  redemptions: number;
  events: number;
  bestMonth: MonthBucket | null;
  worstMonth: MonthBucket | null;
};

export type MonthBucket = { ymKey: string; label: string; value: number };

const MONTHS_SHORT = [
  "янв", "фев", "мар", "апр", "май", "июн",
  "июл", "авг", "сен", "окт", "ноя", "дек",
];

function ymKeyOf(iso: string): string {
  return iso.slice(0, 7); // YYYY-MM
}

function monthLabel(ymKey: string): string {
  const [y, m] = ymKey.split("-");
  return `${MONTHS_SHORT[Number(m) - 1]} ${y.slice(2)}`;
}

// Полный набор помесячных сумм реализованного P&L, отсортированный по дате.
// Используется и для баров, и для поиска лучшего/худшего месяца.
export function allMonthBuckets(events: PnlEvent[]): MonthBucket[] {
  const byKey = new Map<string, number>();
  for (const e of events) {
    const k = ymKeyOf(e.date);
    byKey.set(k, (byKey.get(k) ?? 0) + e.realised_rub);
  }
  return [...byKey.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([ymKey, value]) => ({ ymKey, label: monthLabel(ymKey), value }));
}

// Последние `count` месяцев вплоть до месяца `now` включительно — с нулями
// для месяцев без событий, чтобы бар-чарт не «схлопывал» пустые периоды.
export function recentMonthBuckets(
  events: PnlEvent[],
  now: Date,
  count = 12,
): MonthBucket[] {
  const byKey = new Map<string, number>();
  for (const e of events) {
    const k = ymKeyOf(e.date);
    byKey.set(k, (byKey.get(k) ?? 0) + e.realised_rub);
  }
  const out: MonthBucket[] = [];
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ymKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    out.push({ ymKey, label: monthLabel(ymKey), value: byKey.get(ymKey) ?? 0 });
  }
  return out;
}

export function summarize(events: PnlEvent[], now: Date): StatsSummary {
  const yearPrefix = String(now.getFullYear());
  const monthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  let total = 0;
  let ytd = 0;
  let month = 0;
  let coupons = 0;
  let sells = 0;
  let redemptions = 0;
  for (const e of events) {
    total += e.realised_rub;
    if (e.date.startsWith(yearPrefix)) ytd += e.realised_rub;
    if (e.date.startsWith(monthPrefix)) month += e.realised_rub;
    const src = sourceOf(e.trade_type);
    if (src === "coupons") coupons += e.realised_rub;
    else if (src === "sells") sells += e.realised_rub;
    else redemptions += e.realised_rub;
  }

  const buckets = allMonthBuckets(events);
  let bestMonth: MonthBucket | null = null;
  let worstMonth: MonthBucket | null = null;
  for (const b of buckets) {
    if (bestMonth === null || b.value > bestMonth.value) bestMonth = b;
    if (worstMonth === null || b.value < worstMonth.value) worstMonth = b;
  }

  return {
    total,
    ytd,
    month,
    coupons,
    sells,
    redemptions,
    events: events.length,
    bestMonth,
    worstMonth,
  };
}

export type IssuerTotal = { name: string; value: number };

// Топ эмитентов по реализованному P&L. Имя события уже человекочитаемое
// (secname с бэка), группируем по нему.
export function topIssuers(events: PnlEvent[], n = 3): IssuerTotal[] {
  const byName = new Map<string, number>();
  for (const e of events) {
    byName.set(e.name, (byName.get(e.name) ?? 0) + e.realised_rub);
  }
  return [...byName.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, n);
}
